/**
 * Redux async actions (src/store/actions/teachersActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";
import { websocketConnected, websocketMessageReceived, websocketDisconnected, retireMaterialScope } from '../slices/teacherSlice';
import { toAdminRequestError } from '../../utils/adminRequestError';

const normalizeMaterialsTeacherPagination = (pagination, { page, limit, itemCount }) => {
  const safePagination = pagination && typeof pagination === 'object' ? pagination : {};
  const requestedPage = Number(page) || 1;
  const requestedLimit = Number(limit) || 20;
  const currentPage = Number(safePagination.currentPage ?? safePagination.page ?? requestedPage) || requestedPage;
  const pageSize = Number(safePagination.limit ?? safePagination.pageSize ?? requestedLimit) || requestedLimit;
  const totalCount = Number(safePagination.totalCount ?? safePagination.total ?? itemCount) || 0;
  const totalPages =
    Number(safePagination.totalPages ?? Math.max(1, Math.ceil(totalCount / Math.max(pageSize, 1)))) || 1;

  return {
    ...safePagination,
    currentPage,
    totalPages,
    totalCount,
    hasNextPage: Boolean(safePagination.hasNextPage ?? currentPage < totalPages),
    hasPrevPage: Boolean(safePagination.hasPrevPage ?? currentPage > 1),
    limit: pageSize,
  };
};

const normalizeTeacherMaterialBooking = (booking) => {
  const materialCount = Number(
    booking?.materialCount ?? (Array.isArray(booking?.materials) ? booking.materials.length : 0)
  ) || 0;

  return {
    ...booking,
    materialCount,
    hasMaterials: materialCount > 0,
  };
};

const toTeacherMaterialsError = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  const status = Number(error?.response?.status ?? error?.status ?? 0) || null;
  const message = typeof responseData === 'string'
    ? responseData
    : responseData?.message || responseData?.error || error?.message || fallbackMessage;

  return {
    message,
    status,
    code: responseData?.code || error?.code || null,
  };
};


const DEFAULT_PUBLIC_TEACHER_CATALOGUE_LIMIT = 5;
const MAX_PUBLIC_TEACHER_CATALOGUE_LIMIT = 50;

// Keep the UI defensive when a stale test deployment or a misconfigured public
// API sends fixture annotations that the backend should already have removed.
// This matcher is intentionally narrower than a generic "test" check so a
// legitimate teacher description is not hidden from the catalogue.
const INTERNAL_PUBLIC_COPY_PATTERN = [
  /\bcodex(?:[_-][a-z0-9]+)*\b/i,
  /\bsynthetic(?:[-_\s][a-z0-9]+)*\b/i,
  /\bfixture\b/i,
  /\btest[-_ ]?(?:db|env|fixture|account|teacher)\b/i,
  /\bno\s+real\s+person\b/i,
  /\bandroid\s+qa\s+(?:teacher|fixture)\b/i,
];

const sanitizePublicCopy = (value) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return '';

  const withoutBracketedMarker = text
    .replace(/\[[^\]]*(?:codex|synthetic|fixture|test[-_ ]?(?:db|env|fixture|account|teacher)|no\s+real\s+person)[^\]]*\]\s*/gi, '')
    .replace(/\bsynthetic\s+teacher\s+profile\s*\d*\s*[;:.]?\s*/gi, '')
    .replace(/\bsynthetic\s+android\s+qa\s+fixture\s*[;:.]?\s*/gi, '')
    .replace(/\bno\s+real\s+person\s*[;:.]?\s*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return INTERNAL_PUBLIC_COPY_PATTERN.some((pattern) => pattern.test(withoutBracketedMarker))
    ? ''
    : withoutBracketedMarker;
};

const parsePublicCataloguePositiveInteger = (value, fallback, max) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (!/^\d+$/.test(String(value))) return null;

  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= max ? parsed : null;
};

const isPublicTeacherDto = (teacher) => (
  teacher
  && typeof teacher === 'object'
  && (typeof teacher._id === 'string' || typeof teacher._id === 'number')
  && typeof teacher.Username === 'string'
  && Array.isArray(teacher.Profile_Image)
  && teacher.Profile_Image.every((image) => typeof image === 'string')
  && typeof teacher.Short_Title === 'string'
  && typeof teacher.Description === 'string'
  && teacher.availabilitySummary
  && typeof teacher.availabilitySummary === 'object'
  && Number.isSafeInteger(teacher.availabilitySummary.availableCount)
  && teacher.availabilitySummary.availableCount >= 0
  && (
    teacher.availabilitySummary.nextAvailableAt === null
    || (
      typeof teacher.availabilitySummary.nextAvailableAt === 'string'
      && Number.isFinite(new Date(teacher.availabilitySummary.nextAvailableAt).getTime())
    )
  )
  && (
    teacher.availabilitySummary.nextAvailableSlots === undefined
    || (
      Array.isArray(teacher.availabilitySummary.nextAvailableSlots)
      && teacher.availabilitySummary.nextAvailableSlots.every((slot) => (
        typeof slot === 'string' && Number.isFinite(new Date(slot).getTime())
      ))
    )
  )
);

const isPublicTeacherCataloguePagination = (pagination, { page, limit, itemCount }) => {
  if (!pagination || typeof pagination !== 'object') return false;

  const total = pagination.total;
  const totalPages = pagination.totalPages;
  if (
    pagination.page !== page
    || pagination.limit !== limit
    || !Number.isSafeInteger(total)
    || total < 0
    || !Number.isSafeInteger(totalPages)
    || totalPages < 0
    || totalPages !== (total === 0 ? 0 : Math.ceil(total / limit))
    || typeof pagination.hasNextPage !== 'boolean'
    || typeof pagination.hasPrevPage !== 'boolean'
    || itemCount > limit
  ) return false;

  return (
    pagination.hasNextPage === (page < totalPages)
    && pagination.hasPrevPage === (page > 1 && totalPages > 0)
  );
};

const toSafePublicTeacherDto = (teacher) => {
  const availabilitySummary = {
    availableCount: teacher.availabilitySummary.availableCount,
    nextAvailableAt: teacher.availabilitySummary.nextAvailableAt,
  };
  if (Array.isArray(teacher.availabilitySummary.nextAvailableSlots)) {
    availabilitySummary.nextAvailableSlots = teacher.availabilitySummary.nextAvailableSlots.slice(0, 3);
  }

  return {
    _id: teacher._id,
    Username: sanitizePublicCopy(teacher.Username),
    Profile_Image: [...teacher.Profile_Image],
    Short_Title: sanitizePublicCopy(teacher.Short_Title),
    Description: sanitizePublicCopy(teacher.Description),
    availabilitySummary,
  };
};

const toSafePublicTeacherCataloguePagination = (pagination) => ({
  page: pagination.page,
  limit: pagination.limit,
  total: pagination.total,
  totalPages: pagination.totalPages,
  hasNextPage: pagination.hasNextPage,
  hasPrevPage: pagination.hasPrevPage,
});

/**
 * Async thunk: fetch5Teachers.
 *
 * Fetches the canonical, paginated public catalogue. The returned payload stays
 * an array for existing consumers; pagination is attached to the fulfilled
 * action metadata for the slice. Response fields are copied from an allow-list
 * before entering Redux so an accidental server projection expansion cannot
 * expose private teacher data in the client store.
 */
export const fetch5Teachers = createAsyncThunk(
  "teacher/fetch5Teachers",
  async ({ page: requestedPage, limit: requestedLimit } = {}, { rejectWithValue, fulfillWithValue }) => {
    const page = parsePublicCataloguePositiveInteger(
      requestedPage,
      1,
      Number.MAX_SAFE_INTEGER
    );
    const limit = parsePublicCataloguePositiveInteger(
      requestedLimit,
      DEFAULT_PUBLIC_TEACHER_CATALOGUE_LIMIT,
      MAX_PUBLIC_TEACHER_CATALOGUE_LIMIT
    );

    if (!page || !limit) {
      return rejectWithValue('Teacher catalogue unavailable');
    }

    try {
      const response = await axios.get('public/teachers', { params: { page, limit } });
      const responseBody = response?.data;
      const teachers = responseBody?.data;
      const hasValidDto = responseBody?.success === true
        && Array.isArray(teachers)
        && teachers.every(isPublicTeacherDto)
        && isPublicTeacherCataloguePagination(responseBody.pagination, {
          page,
          limit,
          itemCount: teachers.length,
        });

      if (!hasValidDto) {
        return rejectWithValue(responseBody?.message || 'Teacher catalogue unavailable');
      }

      const safeTeachers = teachers
        .map(toSafePublicTeacherDto)
        .filter((teacher) => teacher.Username && teacher.Short_Title);

      return fulfillWithValue(
        safeTeachers,
        { pagination: toSafePublicTeacherCataloguePagination(responseBody.pagination) }
      );
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data?.error || error.message || 'Teacher catalogue unavailable'
      );
    }
  }
);

/**
 * Async thunk: fetchTrialTeacher.
 * Fetches public trial availability, optionally scoped to one preferred teacher.
 */
export const fetchTrialTeacher = createAsyncThunk(
  "teacher/fetchTrialTeacher",
  async (params, { rejectWithValue }) => {
    try {
      const teacherId = params?.teacherId;
      const response = teacherId === undefined
        ? await axios.get(`FetchTrialTeacher`)
        : await axios.get(`FetchTrialTeacher`, { params: { teacherId } });
      return { teacher: response.data.teacher, teacherMap: response.data.teacherMap || {} };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch trial teacher');
    }
  }
);

/**
 * Async thunk: fetchTeacherDetails.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchTeacherDetails = createAsyncThunk(
  "teacher/fetchTeacherDetails",
  async (params, { rejectWithValue }) => {
    const id = typeof params === "object" ? params.id : params;
    const isAdminRequest = typeof params === "object" && params.admin === true;
    try {
      const endpoint = isAdminRequest
        ? `admin/teachers/${id}/details`
        : `fetchTeacherDetails/${id}`;
      const response = await axios.get(endpoint);
      const responseBody = response.data || {};
      const teacherData =
        responseBody.teachersDetails ||
        responseBody.teacher ||
        (responseBody.data && !Array.isArray(responseBody.data) ? responseBody.data : null);

      if (!teacherData || typeof teacherData !== 'object') {
        return rejectWithValue(toAdminRequestError({ response: { data: responseBody } }, 'Teacher details unavailable'));
      }
      
      // Store teacher's timezone/format separately — do NOT overwrite the user's selectedTimezone
      if (teacherData.userTimezone) {
        localStorage.setItem('teacherTimezone', teacherData.userTimezone);
      }
      if (teacherData.hourFormat) {
        localStorage.setItem('teacherHourFormat', teacherData.hourFormat);
      }
      
      return teacherData;
    } catch (error) {
      return rejectWithValue(toAdminRequestError(error, 'Teacher details unavailable'));
    }
  }
);

/**
 * Async thunk: fetchTeacherDetailsWithoutSlot.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchTeacherDetailsWithoutSlot = createAsyncThunk(
  "teacher/fetchTeacherDetailsWithoutSlot",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`fetchTeacherDetailswithoutSlot/${id}`);
      return response.data.teachersDetails;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk: fetch1teacher.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetch1teacher = createAsyncThunk(
  "teacher/fetch1teacher",
  async () => {
    const response = await axios.get(`Fetch1teacher`);
    
    return response.data.TeacherDetails;
  }
);

/**
 * Async thunk: GetTeachers.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const GetTeachers = createAsyncThunk("teacher/GetTeachers", async (params = {}, { rejectWithValue }) => {
  try {
    const queryParams = {};
    if (params.archiveScope) {
      queryParams.archiveScope = params.archiveScope;
    }

    const response = await axios.get(`getteachers`, {
      params: queryParams,
    });

    // Handle both array response and wrapped object response
    let teachers = response.data;
    if (response.data && response.data.teachers) {
      teachers = response.data.teachers;
    } else if (response.data && !Array.isArray(response.data)) {
      // If it's an object but not an array, try to get the first array property
      const firstArrayKey = Object.keys(response.data).find(key => Array.isArray(response.data[key]));
      if (firstArrayKey) {
        teachers = response.data[firstArrayKey];
      }
    }

    return Array.isArray(teachers) ? teachers : [];
  } catch (err) {
    console.error('Error fetching teachers:', err);
    return rejectWithValue(toAdminRequestError(err, 'Failed to fetch teachers'));
  }
});

/**
 * Async thunk: setTeacherArchiveStatus.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const setTeacherArchiveStatus = createAsyncThunk(
  "teacher/setTeacherArchiveStatus",
  async ({ teacherId, isArchived }, { rejectWithValue }) => {
    try {
      const endpoint = isArchived ? "archive" : "unarchive";
      const response = await axios.patch(`admin/teachers/${teacherId}/${endpoint}`);

      const teacher =
        response?.data?.entity ||
        response?.data?.teacher ||
        response?.data?.updatedTeacher ||
        response?.data?.data?.teacher ||
        response?.data?.data ||
        null;

      return {
        teacherId,
        isArchived,
        teacher,
      };
    } catch (error) {
      return rejectWithValue({
        teacherId,
        isArchived,
        status: Number(error?.response?.status) || undefined,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to update teacher archive status",
      });
    }
  }
);

/**
 * Async thunk: imageUpload.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const imageUpload = createAsyncThunk(
  "property/imageUpload",
  async (image) => {
    const formData = new FormData();
    formData.append("image", image);
    const response = await axios.post(`update-image`, formData);
    // 
    // 
    return response.data.filename;
  }
);

/**
 * Async thunk: videoUpload.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const videoUpload = createAsyncThunk(
  "teacher/videoUpload",
  async (video) => {
    const formData = new FormData();
    formData.append("video", video);
    try {
      const response = await axios.post(`upload-video`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data.filename;
    } catch (error) {
      console.error("Error uploading video:", error);
      throw error;
    }
  }
);


/**
 * Async thunk: getVideo.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getVideo = createAsyncThunk(
  "teacher/getVideo",
  async (filename, { rejectWithValue }) => {
    try {
      // Use fetch() instead of axios/XHR so that 404s don't appear as console errors
      // (browsers log failed XMLHttpRequests but not failed fetch responses)
      // BUG-004/005: Auth token is in httpOnly cookie, sent automatically via credentials.
      const baseURL = axios.defaults.baseURL || '';
      const url = `${baseURL}video/${filename}`;

      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        return rejectWithValue(`Server error: ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        return rejectWithValue('Received empty video');
      }

      return URL.createObjectURL(blob);
    } catch (error) {
      return rejectWithValue(`Error: ${error.message}`);
    }
  }
);

/**
 * Async thunk: AddNewTeacher.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const AddNewTeacher = createAsyncThunk(
  "property/AddNewTeacher",
  async (formData) => {
    try {
      const response = await axios.post(`Signup_Teacher`, formData);
      // 
      return response.data;
    } catch (err) {
      
    }
  }
);

/**
 * Async thunk: changeTeacherPassword.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const changeTeacherPassword = createAsyncThunk(
  "teacher/changePassword",
  async ({ teacherId, currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`change-password`, {
        teacherId,
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      const payload = error.response?.data;
      if (payload && typeof payload === 'object') {
        return rejectWithValue({
          ...payload,
          status: error.response?.status,
          message: payload.message || payload.error || error.message || 'Failed to change password',
        });
      }
      return rejectWithValue({
        message: payload || error.message || 'Failed to change password',
      });
    }
  }
);

/**
 * Async thunk: DeleteTeacher.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const DeleteTeacher = createAsyncThunk(
  "teacher/deleteTeacher",
  async (teacher_ID, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`Delete_teacher/${teacher_ID}`);
      //
      return response.data;
    } catch (error) {
      return rejectWithValue(toAdminRequestError(error, 'Failed to delete teacher'));
    }
  }
);

/**
 * Async thunk: updateTeacher.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateTeacher = createAsyncThunk(
  "teacher/updateTeacher",
  async ({ teacherId, updatedData, admin = false }, { rejectWithValue }) => {
    
    try {
      const response = await axios.post(
        admin
          ? `admin/teachers/${teacherId}/availability`
          : `Update_Teacher/${teacherId}`,
        updatedData
      );
      
      return response.data;
    } catch (error) {
      // Preserve a structured server rejection for callers using unwrap(). In
      // particular, the availability editor must receive the canonical 409
      // conflict snapshot instead of Redux Toolkit's serialized Axios error.
      if (error?.response?.data) {
        return rejectWithValue({
          ...error.response.data,
          status: Number(error.response.status) || undefined,
        });
      }
      throw error;
    }
  }
);

const toTeacherAvailabilityError = (error, fallbackMessage) => {
  const payload = error?.response?.data;
  const status = Number(error?.response?.status ?? error?.status ?? payload?.status) || undefined;

  if (payload && typeof payload === 'object') {
    return {
      ...payload,
      ...(status ? { status } : {}),
      message: payload.message || payload.error || error?.message || fallbackMessage,
    };
  }

  return {
    ...(status ? { status } : {}),
    message: error?.message || fallbackMessage,
  };
};

/**
 * Read the authenticated teacher availability snapshot used by the editors.
 * This is deliberately separate from fetchTeacherDetails so legacy profile
 * consumers keep their existing DTO and endpoint contract.
 */
export const getTeacherAvailabilitySnapshot = createAsyncThunk(
  'teacher/getTeacherAvailabilitySnapshot',
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`teachers/${teacherId}/availability`);
      return response.data;
    } catch (error) {
      return rejectWithValue(toTeacherAvailabilityError(error, 'Failed to load teacher availability'));
    }
  }
);

/**
 * Replace the authenticated teacher availability snapshot with optimistic
 * concurrency protection. Keep this action canonical and isolated from the
 * legacy Update_Teacher PATCH consumers.
 */
export const updateTeacherAvailabilitySnapshot = createAsyncThunk(
  'teacher/updateTeacherAvailabilitySnapshot',
  async ({ teacherId, availability, expectedRevision }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`teachers/${teacherId}/availability`, {
        availability,
        expectedRevision,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(toTeacherAvailabilityError(error, 'Failed to update teacher availability'));
    }
  }
);

/**
 * Async thunk: Update_Teacher_By_Availability.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Update_Teacher_By_Availability = createAsyncThunk(
  "teacher/Update_Teacher_By_Availability",
  async ({ teacherId, updatedData }, { rejectWithValue }) => {
    try {
      // Keep this legacy thunk on the same canonical route as the active
      // availability editor. The former URL was never registered by the API.
      const response = await axios.post(`Update_Teacher/${teacherId}`, updatedData);
      return {
        ...(response.data || {}),
        teacherId,
        updatedData,
      };
    } catch (error) {
      if (error?.response?.data) {
        return rejectWithValue({
          ...error.response.data,
          status: Number(error.response.status) || undefined,
        });
      }
      return rejectWithValue({
        message: error?.message || 'Failed to update teacher availability',
      });
    }
  }
);

/**
 * Async thunk: Signup_Teacher_By_Admin.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const Signup_Teacher_By_Admin = createAsyncThunk(
  "property/Signup_Teacher_By_Admin",
  async (formData, { rejectWithValue }) => {
    // 
    try {
      const response = await axios.post(`Signup_Teacher_By_Admin`, formData);

      return response;
    } catch (error) {
      
      
      return rejectWithValue(toAdminRequestError(error, 'Failed to create teacher'));
    }
  }
);




/**
 * Async thunk: GetExistingTeacher_Availability.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const GetExistingTeacher_Availability = createAsyncThunk(
  'teacher/GetExistingTeacher_Availability',
  async (params, { rejectWithValue }) => {
    try {
      // Handle both old format (just teacherId) and new format ({ teacherId, monthsLimit, timezone })
      const teacherId = typeof params === 'string' ? params : params.teacherId;
      const monthsLimit = typeof params === 'object' ? params.monthsLimit : null;
      const timezone = typeof params === 'object' ? params.timezone : null;

      let url = `GetExistingTeacher_Availability/${teacherId}`;
      const queryParams = [];
      if (monthsLimit) {
        queryParams.push(`monthsLimit=${encodeURIComponent(monthsLimit)}`);
      }
      if (timezone) {
        queryParams.push(`timezone=${encodeURIComponent(timezone)}`);
      }
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }
      const response = await axios.get(url);
      return response.data; // Return the entire response data
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk: fetchTeacherDetailsWithMonthlyAvailability.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchTeacherDetailsWithMonthlyAvailability = createAsyncThunk(
  'teacher/fetchTeacherDetailsWithMonthlyAvailability',
  async (teacherId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`fetchTeacherDetailsWithMonthlyAvailability/${teacherId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);


/**
 * Async thunk: SearchTeacherbyUsername.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const SearchTeacherbyUsername = createAsyncThunk(
  "teacher/SearchTeacherbyUsername",
  async (input) => {
    // 
    try {
      const response = await axios.post(`SearchTeacherbyUsername/${input}`);
      // 
      return response;
    } catch (err) {
      
      return err.response.data
    }
  }
);


/**
 * Async thunk: fetchTeacherBookings.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchTeacherBookings = createAsyncThunk(
  "teacher/fetchTeacherBookings",
  async (id) => {
    
    try {
      const response = await axios.post(`fetchTeacherBookings/${id}`);
      
      return response;
    } catch (err) {
      
      return err.response.data;
    }
  }
);

/**
 * Async thunk: uploadToLocal.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const uploadToLocal = createAsyncThunk(
  'teacher/uploadToLocal',
  async ({ formData, bookingId }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { bookingId }
      });
      return response.data.files?.[0] || response.data.file || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


/**
 * Async thunk: listLocalFiles.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const listLocalFiles = createAsyncThunk(
  'teacher/listLocalFiles',
  async (arg, api) => {
    const bookingId = typeof arg === 'object' ? arg.bookingId : arg;
    const scope = getMaterialRequestScope(api.getState(), arg?.studentId);
    try {
      const response = await axios.get(`/list?bookingId=${bookingId}`, { signal: api.signal });
      assertMaterialRequestActive(api, scope, 'list', bookingId, arg?.studentId);
      return { bookingId, files: response.data.files }; // Return bookingId and files
    } catch (error) {
      assertMaterialRequestActive(api, scope, 'list', bookingId, arg?.studentId);
      return api.rejectWithValue(error.response?.data || error.message);
    }
  },
  { getPendingMeta: ({ arg }, { getState }) => ({ materialScope: getMaterialRequestScope(getState(), arg?.studentId) }) }
);

// New optimized API to get teacher bookings with material counts
/**
 * Async thunk: getMaterialsTeacher.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getMaterialsTeacher = createAsyncThunk(
  'teacher/getMaterialsTeacher',
  async ({ teacherId, page = 1, limit = 20, date, studentId, archiveScope, timezone }, { rejectWithValue }) => {
    try {
      let url = `/materials-teacher?teacherId=${teacherId}&page=${page}&limit=${limit}`;

      // Add date filter if provided
      if (date) {
        url += `&date=${date}`;
      }

      // Add student filter if provided
      if (studentId) {
        url += `&studentId=${studentId}`;
      }

      if (archiveScope) {
        url += `&archiveScope=${archiveScope}`;
      }

      if (timezone) {
        url += `&timezone=${encodeURIComponent(timezone)}`;
      }

      const response = await axios.get(url);
      const responseBody = response.data || {};
      if (responseBody.success === false) {
        return rejectWithValue(toTeacherMaterialsError({
          response: { data: responseBody, status: response.status },
        }, 'Failed to load teacher materials'));
      }

      const data = Array.isArray(responseBody.data) ? responseBody.data : [];
      return {
        data,
        pagination: normalizeMaterialsTeacherPagination(responseBody.pagination, {
          page,
          limit,
          itemCount: data.length,
        }),
        archiveScope: archiveScope || 'active',
      };
    } catch (error) {
      return rejectWithValue(toTeacherMaterialsError(error, 'Failed to load teacher materials'));
    }
  }
);

/**
 * Async thunk: deleteLocalFile.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const deleteLocalFile = createAsyncThunk(
  'teacher/deleteLocalFile',
  async ({ bookingId, fileName }, { rejectWithValue }) => {
    try {
      await axios.delete(`/delete/${bookingId}/${fileName}`);
      return fileName;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ============ OPTIMIZED MATERIALS ACTIONS FOR PERFORMANCE ============

// Fetch bookings with materials metadata (paginated and optimized)
/**
 * Async thunk: fetchBookingsWithMaterials.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchBookingsWithMaterials = createAsyncThunk(
  'teacher/fetchBookingsWithMaterials',
  async ({ teacherId, page = 1, limit = 20, sortBy = 'startTime', sortOrder = 'desc' }, { rejectWithValue }) => {
    try {
      // `/bookings-with-materials` is a legacy, non-paginated student/admin
      // surface. Teacher materials use the canonical scoped endpoint below;
      // keeping this thunk on that contract prevents the optimized page from
      // calling an endpoint whose response shape it cannot consume.
      const response = await axios.get('/materials-teacher', {
        params: { teacherId, page, limit, sortBy, sortOrder }
      });
      const responseBody = response.data || {};
      if (responseBody.success === false) {
        return rejectWithValue(responseBody.message || responseBody.error || 'Failed to load teacher materials');
      }

      const bookings = Array.isArray(responseBody.data)
        ? responseBody.data.map(normalizeTeacherMaterialBooking)
        : [];

      return {
        ...responseBody,
        bookings,
        data: bookings,
        pagination: normalizeMaterialsTeacherPagination(responseBody.pagination, {
          page,
          limit,
          itemCount: bookings.length,
        }),
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get materials statistics using aggregation pipeline
/**
 * Async thunk: fetchMaterialsStatistics.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchMaterialsStatistics = createAsyncThunk(
  'teacher/fetchMaterialsStatistics',
  async (teacherId, { rejectWithValue }) => {
    try {
      // There is no `/materials-statistics` backend route. Compose the
      // statistics from the two existing, authorized teacher material
      // contracts instead of manufacturing a client-only API.
      const [teacherBookingsResponse, materialBookingsResponse] = await Promise.all([
        axios.get('/materials-teacher', { params: { teacherId, page: 1, limit: 1 } }),
        axios.get('/bookings-with-materials', { params: { teacherId } }),
      ]);
      const teacherBookingsBody = teacherBookingsResponse.data || {};
      const materialBookingsBody = materialBookingsResponse.data || {};
      if (teacherBookingsBody.success === false || materialBookingsBody.success === false) {
        return rejectWithValue(
          teacherBookingsBody.message
            || materialBookingsBody.message
            || 'Failed to load teacher material statistics'
        );
      }

      const materialBookings = Array.isArray(materialBookingsBody.data)
        ? materialBookingsBody.data
        : [];
      const totalFiles = materialBookings.reduce((total, booking) => {
        const count = Number(
          booking?.materialCount ?? (Array.isArray(booking?.materials) ? booking.materials.length : 0)
        );
        return total + (Number.isFinite(count) && count > 0 ? count : 0);
      }, 0);

      return {
        totalBookings: Number(teacherBookingsBody.pagination?.totalCount || 0),
        bookingsWithMaterials: materialBookings.length,
        totalFiles,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Bulk fetch materials for multiple bookings
/**
 * Async thunk: bulkFetchMaterials.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const bulkFetchMaterials = createAsyncThunk(
  'teacher/bulkFetchMaterials',
  async (bookingIds, { rejectWithValue }) => {
    try {
      const ids = [...new Set((Array.isArray(bookingIds) ? bookingIds : [bookingIds])
        .map((bookingId) => String(bookingId || '').trim())
        .filter(Boolean))];
      if (!ids.length) return { materialsByBooking: {} };

      const entries = await Promise.all(ids.map(async (bookingId) => {
        const response = await axios.get('/list', { params: { bookingId } });
        const files = Array.isArray(response.data?.files) ? response.data.files : [];
        return [bookingId, files];
      }));

      return { materialsByBooking: Object.fromEntries(entries) };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get materials for specific booking
/**
 * Async thunk: fetchBookingMaterials.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const fetchBookingMaterials = createAsyncThunk(
  'teacher/fetchBookingMaterials',
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await axios.get('/list', { params: { bookingId } });
      const responseBody = response.data || {};
      return {
        ...responseBody,
        materials: Array.isArray(responseBody.files) ? responseBody.files : [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ============ LEGACY ACTIONS (Updated to use optimized endpoints) ============

/**
 * Async thunk: downloadLocalFile.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
// Material scope is UI request ownership, not an authorization decision. Teacher
// callers without a student identity still use the normal authenticated API.
export function getMaterialRequestScope(state, studentId) {
  const user = state?.students?.user;
  return JSON.stringify([user?._id || null, user?.UserType || null,
    user?.accountType || null, user?.parentUserId?._id || user?.parentUserId || null,
    studentId || null]);
}

function assertMaterialRequestActive(api, scope, kind, bookingId, studentId) {
  const state = api.getState();
  const requests = kind === 'list' ? state?.teachers?.materialListRequests : state?.teachers?.materialDownloadRequests;
  const owner = kind === 'list' ? requests?.[bookingId] : requests?.[api.requestId];
  const actorRetired = getMaterialRequestScope(state, studentId) !== scope;
  if (actorRetired && state?.teachers?.materialScope === scope) api.dispatch(retireMaterialScope(scope));
  if (api.signal.aborted || actorRetired
    || (requests && (state.teachers.materialScope !== scope || owner !== api.requestId))) {
    const error = new Error('Material request retired');
    error.name = 'AbortError';
    throw error;
  }
}

export const downloadLocalFile = createAsyncThunk(
  'teacher/downloadLocalFile',
  async ({ bookingId, fileName, originalName, studentId }, api) => {
    const scope = getMaterialRequestScope(api.getState(), studentId);
    try {
      const response = await axios.get(`/download/${encodeURIComponent(bookingId)}/${encodeURIComponent(fileName)}`, {
        responseType: 'blob', // Important for file downloads
        signal: api.signal,
      });
      assertMaterialRequestActive(api, scope, 'download', bookingId, studentId);
      const safeName = (value) => {
        if (typeof value !== 'string') return '';
        // Remove ASCII controls explicitly, preserving Unicode display names
        // without a control-character regex or a disabled lint rule.
        return Array.from(value.split(/[\\/]/).pop())
          .filter((character) => {
            const code = character.codePointAt(0);
            return code > 31 && code !== 127;
          })
          .join('').replace(/[<>:"|?*]/g, '').trim().replace(/[. ]+$/, '');
      };
      const disposition = response.headers?.get?.('content-disposition')
        || response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'] || '';
      const encodedName = disposition.match(/filename\*\s*=\s*UTF-8'[^']*'([^;]+)/i);
      const plainName = disposition.match(/(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;]*))/i);
      let serverName = '';
      try {
        serverName = encodedName ? decodeURIComponent(encodedName[1].trim()) : '';
      } catch (_) { /* Fall back to the plain header or original display name. */ }
      serverName = safeName(serverName || plainName?.[1] || plainName?.[2]);
      // Storage keys identify the API resource, never the user's download.
      const displayName = safeName(originalName);
      const opaqueName = /^(?:[a-f\d]{24,64}|[a-f\d]{8}(?:-[a-f\d]{4}){3}-[a-f\d]{12})(?:\.[^.]*)?$/i;
      if (opaqueName.test(serverName) || (serverName === safeName(fileName) && displayName && displayName !== serverName)) serverName = '';
      const downloadName = serverName || (opaqueName.test(displayName) ? '' : displayName) || 'material';
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      assertMaterialRequestActive(api, scope, 'download', bookingId, studentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      try {
        assertMaterialRequestActive(api, scope, 'download', bookingId, studentId);
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        // Allow the browser to consume the object URL before releasing it.
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
      return downloadName; // The existing slice owns the localized success toast.
    } catch (error) {
      assertMaterialRequestActive(api, scope, 'download', bookingId, studentId);
      return api.rejectWithValue(error.response?.data || error.message);
    }
  },
  { getPendingMeta: ({ arg }, { getState }) => ({ materialScope: getMaterialRequestScope(getState(), arg?.studentId) }) }
);

/**
 * Async thunk: downloadMultipleFiles.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const downloadMultipleFiles = createAsyncThunk(
  'teacher/downloadMultipleFiles',
  async ({ bookingId, fileNames }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/download-multiple/${bookingId}`, { fileNames }, { 
        responseType: 'blob',
        timeout: 30000 // 30 seconds timeout
      });
      
      const contentType = response.headers['content-type'];
      if (contentType === 'application/json') {
        // If the response is JSON, it's likely an error message
        const reader = new FileReader();
        /**
         * Onload.
         *
         * Named helper used to keep this module readable and testable.
         */
        reader.onload = () => {
          const errorMessage = JSON.parse(reader.result);
          throw new Error(errorMessage.message || 'Unknown error occurred');
        };
        reader.readAsText(response.data);
      } else {
        // It's a file, proceed with download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${bookingId}_files.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      
      return fileNames;
    } catch (error) {
      console.error('Error downloading files:', error);
      return rejectWithValue(error.message || 'Failed to download files');
    }
  }
);

// Global teacher socket instance - outside Redux to avoid serialization issues
let teacherSocket = null;

/**
 * Async thunk: connectWebSocket.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const connectWebSocket = createAsyncThunk(
  'teacher/connectWebSocket',
  async (userId, { dispatch, getState }) => {
    // Don't create duplicate connections
    if (teacherSocket && teacherSocket.connected) {
      
      return { connected: true };
    }

    // Validate userId before attempting connection
    if (!userId) {
      throw new Error('User ID is required for WebSocket connection');
    }

    const { io } = await import('socket.io-client');
    
    // Disconnect existing socket if any
    if (teacherSocket) {
      teacherSocket.removeAllListeners();
      teacherSocket.disconnect();
    }
    
    const socketUrl = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || 'https://speakable.online';
    
    
    
    teacherSocket = io(socketUrl, {
      query: { userId },
      withCredentials: true,
      transports: ['polling', 'websocket'],
      upgrade: true,
      rememberUpgrade: true,
      autoConnect: true,
      forceNew: false,
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000
    });

    return new Promise((resolve, reject) => {
      let resolved = false;

      teacherSocket.on('connect', () => {
       
        dispatch(websocketConnected());
        if (!resolved) {
          resolved = true;
          resolve({ connected: true });
        }
      });

      teacherSocket.on('disconnect', (reason) => {
       
        dispatch(websocketDisconnected());
      });

      teacherSocket.on('connect_error', (error) => {
        console.error('Teacher WebSocket connection error:', error);
        if (!resolved) {
          resolved = true;
          reject(new Error(`Connection failed: ${error.message}`));
        }
      });

      teacherSocket.on('error', (error) => {
        console.error('Teacher WebSocket error:', error);
      });

      // Handle incoming messages
      teacherSocket.onAny((eventName, ...args) => {
        dispatch(websocketMessageReceived({ eventName, data: args }));
      });

      // Set timeout for connection attempt
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error('Connection timeout'));
        }
      }, 20000);
    });
  }
);

// Helper function to get the teacher socket
/**
 * Get Teacher Socket.
 *
 * Named helper used to keep this module readable and testable.
 */
export const getTeacherSocket = () => teacherSocket;

// WebSocket send message action
/**
 * Async thunk: sendWebSocketMessage.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const sendWebSocketMessage = createAsyncThunk(
  'teacher/sendWebSocketMessage',
  async ({ eventName, data }) => {
    if (teacherSocket && teacherSocket.connected) {
      teacherSocket.emit(eventName, data);
      return { eventName, data };
    } else {
      throw new Error('Teacher WebSocket is not connected');
    }
  }
);

/**
 * Async thunk: joinRoom.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const joinRoom = createAsyncThunk(
  "teacher/joinRoom",
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`teacher-joined/${bookingId}`);
      return { bookingId, response: response.data };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk: leaveRoom.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const leaveRoom = createAsyncThunk(
  "teacher/leaveRoom",
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`teacher-left/${bookingId}`);
      return { bookingId, response: response.data };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk: updateTeacherDetails.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const updateTeacherDetails = createAsyncThunk(
  "teacher/updateTeacherDetails",
  async ({ teacherId, updatedData, admin = false }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        admin
          ? `admin/teachers/${teacherId}/details`
          : `update-teacher-details/${teacherId}`,
        updatedData
      );
      
      return response.data;
    } catch (error) {
      console.error("Error updating teacher details:", error);
      return rejectWithValue(toAdminRequestError(error, 'Failed to update teacher'));
    }
  }
);


/**
 * Async thunk: createBugReport.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const createBugReport = createAsyncThunk(
  "bugReport/create",
  async (bugData, { rejectWithValue }) => {
    try {
      const response = await axios.post('report', bugData);
      return response.data.bugReport;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk: getBugReports.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getBugReports = createAsyncThunk(
  "bugReport/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('reports');
      return response.data.bugReports;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

/**
 * Async thunk: getSingleBugReport.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getSingleBugReport = createAsyncThunk(
  "bugReport/getSingle",
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`report/${reportId}`);
      return response.data.bugReport;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
